/**
 * useSurveyStepTracker — module-scoped survey-step state.
 *
 * Surfaces `currentSurveyStepId` (read by `useFunnelAbandon`, written by
 * `useSurvey`) plus a `dedupedAbandons` Set that the abandon-debouncer
 * uses to enforce "at most one effective abandon per step per page".
 *
 * Single shared object — every consumer sees the same ref + Set + the
 * same function references, so a Vitest `vi.spyOn(tracker, ...)`
 * intercepts calls from any other consumer (the abandon composable in
 * particular).
 */

import { ref, type Ref } from "vue";

/** Active survey step id, set by `useSurvey` on each step transition.
 *  Consumed by `useFunnelAbandon` so the abandon trigger knows which step
 *  is at risk. Updated via `setCurrentStepId(id | null)` — callers pass
 *  `null` on step completion so a hidden timer that fires later won't
 *  re-arm against an already-advanced step. */
const currentSurveyStepId: Ref<string | null> = ref(null);

/** Per-page debouncer for abandon emissions: the abandon trigger fires
 *  from multiple sources (visibilitychange timer, route-change guard) and
 *  the contract is "at most one effective abandon per step per page". */
const dedupedAbandons = new Set<string>();

/** Single shared object. All consumers receive the same reference, so a
 *  spy installed in tests intercepts calls from production code paths. */
const tracker = {
    currentSurveyStepId,

    setCurrentStepId(stepId: string | null): void {
        currentSurveyStepId.value = stepId;
    },

    /**
     * Returns true if this stepId has not yet been "marked abandoned" in
     * this page lifetime; flips it to abandoned-already as a side effect.
     */
    tryMarkAbandoned(stepId: string): boolean {
        const key = `survey_step_abandoned:${stepId}`;
        if (dedupedAbandons.has(key)) return false;
        dedupedAbandons.add(key);
        return true;
    },

    /** Reset the per-page dedup state. Used by tests; production calls
     *  this zero times — the page lifetime *is* the boundary. */
    resetAbandonDedup(): void {
        dedupedAbandons.clear();
    },
};

export function useSurveyStepTracker() {
    return tracker;
}
